import React from 'react';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardActions from '@material-ui/core/CardActions';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
// Components
import ErrorMessage from '../../../components/error-message';
import { Typobold } from '../../../components/typobold';
import Listing from '../../../components/listing';
import FidStep from './fid-step';
// Modules
import { Company, Program } from '../../../redux/reducers/api-types';
import { actions as companyActions } from '../../../redux/reducers/companies';
import { ErrorPayload, selectors as errorSelectors } from '../../../redux/reducers/errors';
// Ressources
import DateUtils from '../../../utils/dateUtils';
import UserUtils, { AccountRight, RightRank } from '../../../utils/userUtils';
import { clsx } from '../../../utils/htmlUtils';

interface CompanyFidProps extends WithTranslation {
    company: Company;
    program?: Program;
    rightCheck: AccountRight;
    deleteProgramError: ErrorPayload;
    deleteProgram: (id, idCompany) => void;
}

const CompanyFid = (props: CompanyFidProps): JSX.Element => {
    const classes = useStyles();
    const { t, company, program, rightCheck, deleteProgram, deleteProgramError } = props;
    const { id: idCompany, uuid: uuidCompany } = company;

    if (!program) {
        return null;
    }

    const { id, name, start_date, end_date, deleted_at, type, steps } = program;

    const canWriteFid: boolean = UserUtils.checkRights({
        name: rightCheck,
        mode: RightRank.W,
    });

    const handleDeleteProgram = () => {
        deleteProgram(id, idCompany);
    };

    const isParentProgram = program.id_company !== uuidCompany;

    const renderHeader = (
        <div>
            <ErrorMessage payload={deleteProgramError} />
            <Typobold>
                <>{`${name} ${isParentProgram ? `(${t('parent')})` : ''} - ${t(
                    'communication.fid.form.type.' + type,
                )}`}</>
            </Typobold>
            <div>{`${t('communication.fid.form.enable')} ${DateUtils.stringToDate(start_date)}`}</div>
            {end_date && <div>{`${t('communication.fid.form.disable')} ${DateUtils.stringToDate(end_date)}`}</div>}
            {deleted_at && <div>{`${t('communication.fid.form.disabled')} ${DateUtils.stringToDate(deleted_at)}`}</div>}
        </div>
    );

    const renderDetails = (
        <Listing
            datas={steps?.map((step, index) => {
                return (
                    <div
                        key={`${step.threshold} ${step.reward_type} ${index}`}
                        style={{
                            flex: 'inherit',
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <FidStep step={step} />
                    </div>
                );
            })}
        />
    );

    let classeColor = '';
    if (deleted_at) {
        classeColor = classes.disabled;
    } else {
        if (end_date && new Date(end_date) < new Date()) {
            classeColor = classes.passed;
        }
    }

    return (
        <Card className={clsx(classes.root, isParentProgram && classes.parent, classeColor)}>
            <CardContent className={classes.content}>
                {deleted_at ? (
                    <Accordion>
                        <AccordionSummary
                            style={{ flexDirection: 'inherit' }}
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls={`fid-${id}`}
                            id={`id-${id}`}
                        >
                            {renderHeader}
                        </AccordionSummary>
                        <AccordionDetails style={{ flexDirection: 'column' }}>{renderDetails}</AccordionDetails>
                    </Accordion>
                ) : (
                    <>
                        {renderHeader}
                        {renderDetails}
                    </>
                )}
            </CardContent>
            <CardActions className={classes.rightActions}>
                {canWriteFid && !isParentProgram && !deleted_at && (
                    <Button onClick={() => handleDeleteProgram()} variant="contained" color={'secondary'}>
                        {t('common:delete')}
                    </Button>
                )}
            </CardActions>
        </Card>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: { padding: 16, marginTop: 8 },
        content: { display: 'flex', flexWrap: 'wrap', padding: 0, flexDirection: 'column' },
        rightActions: {
            justifyContent: 'flex-end',
        },
        disabled: { backgroundColor: theme.palette.colors.disabled + ' !important' },
        passed: { backgroundColor: theme.palette.colors.passed + ' !important' },
        parent: { backgroundColor: theme.palette.success.light },
    }),
);

const mapStateToProps = (state) => ({
    deleteProgramError: errorSelectors.deleteProgramError(state),
});

const mapDispatchToProps = {
    deleteProgram: companyActions.deleteProgram,
};

export default connect(mapStateToProps, mapDispatchToProps)(withTranslation(['company', 'common'])(CompanyFid));
